<script lang="ts" setup>
import { onMounted, ref } from "vue";
import ShoppingCart from "../../components/shoppingCart/shoppingCart.vue";
import {
  CheckoutStages,
  CheckoutSteps,
} from "../../services/models/checkout.models";
import {
  Cart,
  PlaceholderAddressModel,
} from "../../utils/API/Cart/cartAPISchema";
import Checkout from "../../components/checkout/checkout.vue";
import Verification from "../../components/verification/verification.vue";
import { useCheckoutStateStore } from "../../components/checkout/stores/checkoutStateStore";
import { useShoppingCartStore } from "../../components/shoppingCart/stores/shoppingCartStore";
import { useFavoritesStore } from "../../stores/favoritesStore";
import CheckoutService from "../../services/checkout.service";
import CheckoutPageLoading from "../../components/shoppingCart/checkoutPageLoading.vue";

const props = defineProps<{
  ksps: string | null; // JSON Content from NewCheckoutIndex.cshtml
  receiptPageUrl: string | null;
  netsEasyApiCheckoutKey: string | null;
  paymentId: any // Payment ID from Nets Easy
  maxCartSize?: string;
  initialLineItemCount?: string; // Number of line items in the cart when the component is mounted
  paymentLogos: string;
}>();

const checkoutStateStore = useCheckoutStateStore();
const shoppingCartStore = useShoppingCartStore();
const favoritesStore = useFavoritesStore();
const cart = ref<Cart>();
const customerInformation = ref<PlaceholderAddressModel>();

/**
 * Initializes the shopping cart by setting the loading states for the checkout and upsell stores,
 * then fetching the shopping cart and upsell model data from the API.
 *
 * @async
 * @function initShoppingCart
 * @returns {Promise<void>} A promise that resolves when the shopping cart and upsell model data have been fetched and the loading states have been updated.
 */
const initShoppingCart = async () => {
    shoppingCartStore.setCart(null);

  if (props.receiptPageUrl) {
    checkoutStateStore.setReceiptPageUrl(props.receiptPageUrl);
  }

  if (props.netsEasyApiCheckoutKey) {
    checkoutStateStore.setNetsEasyApiCheckoutKey(props.netsEasyApiCheckoutKey);
  }

  if (props.paymentLogos) {
    shoppingCartStore.setPaymentLogos(JSON.parse(props.paymentLogos));
  }

  // If a paymentId exists already at page load, we assume the user is returning to the checkout
  // and we set the checkout stage and step accordingly.
  const paymentId = JSON.parse(props.paymentId)?.value;
  if (paymentId != null) {
    checkoutStateStore.setPaymentId(paymentId);
    checkoutStateStore.setCheckoutStage(CheckoutStages.Checkout);
    checkoutStateStore.setCheckoutStep(CheckoutSteps.Payment);
    return;
  }

  // Need to explicitly set loading of cart to trigger skeleton loaders
  checkoutStateStore.setIsLoadingCart(true);
  checkoutStateStore.setIsLoading(true);
  checkoutStateStore.setCheckoutStage(CheckoutStages.ShoppingCart);

  const newCart = await CheckoutService.apiGetShoppingCart();
  if (newCart) {
    shoppingCartStore.setCart(newCart);
  }

  checkoutStateStore.setIsLoadingCart(false);
  checkoutStateStore.setIsLoading(false);

  if (shoppingCartStore.cart?.isAuthenticated && !favoritesStore.favorites) {
    favoritesStore.getFavorites();
  }
};

onMounted(() => {
  initShoppingCart();
});
</script>

<template>
  <div>
    <CheckoutPageLoading
      v-if="
        checkoutStateStore.currentCheckoutStage ===
        CheckoutStages.InitialPageLoad
      "
    ></CheckoutPageLoading>
    <ShoppingCart
      v-if="
        checkoutStateStore.currentCheckoutStage === CheckoutStages.ShoppingCart
      "
      :ksps="props.ksps"
      :max-cart-size="props.maxCartSize"
      :initial-line-item-count="props.initialLineItemCount"
      :is-quick-add="true"
    />
    <Verification
      v-if="
        checkoutStateStore.currentCheckoutStage === CheckoutStages.Verification
      "
    ></Verification>
    <Checkout
      v-if="checkoutStateStore.currentCheckoutStage === CheckoutStages.Checkout"
      :customer-information="customerInformation"
      :cart="cart"
    />
  </div>
</template>
